/**
 * The contract between the Thottbot addon (addon/Thottbot) and the site: version constants,
 * the Forever build gate, and the zod schema of the SavedVariables file the addon writes
 * (`ThottbotDB`). Record keys are stringified game ids; per-entity keys are the short names
 * the addon uses to keep the file small.
 */
import { z } from 'zod';
import { compareBuilds } from './artifact-types.js';

/** Must equal `## Version:` in addon/Thottbot/Thottbot.toc (asserted by apps/api tests). */
export const ADDON_VERSION = '0.1.4';
/** Uploads from addon versions below the admin-set minimum are refused; this is the default. */
export const ADDON_MIN_VERSION_DEFAULT = '0.1.0';
/**
 * Storage key of the addon zip in the image bucket (served on img.thottbot4ever.com, or
 * /uploads locally); `addon/release.sh` uploads it there and `/api/addon/info` links it.
 */
export function addonZipKey(version: string = ADDON_VERSION): string {
  return `addon/Thottbot-${version}.zip`;
}
/** WoW: Forever client builds are the 1.60 line; Classic Era is 1.15. */
export const FOREVER_MIN_BUILD = '1.60.0.0';
export const MAX_ADDON_UPLOAD_BYTES = 25 * 1024 * 1024;
/** Per top-level table; guards memory and junk artifacts. */
export const MAX_ADDON_RECORDS_PER_TABLE = 50_000;
export const MAX_SIGHTINGS_PER_ENTITY = 200;
/** Screenshot records one addon file may carry; the addon drops the oldest beyond this. */
export const MAX_ADDON_SHOTS = 50;

/** Only `applied` uploads feed aggregates. */
export const ADDON_UPLOAD_STATUSES = ['processing', 'applied', 'failed', 'superseded', 'reverted'] as const;
export type AddonUploadStatus = (typeof ADDON_UPLOAD_STATUSES)[number];
/** Where an upload came from: the manual upload page or the folder watcher (`/addon/watch`). */
export const ADDON_UPLOAD_SOURCES = ['manual', 'watcher'] as const;
export type AddonUploadSource = (typeof ADDON_UPLOAD_SOURCES)[number];
/**
 * What kind of file an upload holds: the addon's SavedVariables, one of the client's WDB
 * caches (quests, creatures, game objects) or its hotfix cache (`DBCache.bin`). Detected from
 * the first bytes of the file, never from its name.
 */
export const ADDON_UPLOAD_KINDS = ['addon', 'questcache', 'creaturecache', 'gameobjectcache', 'hotfix'] as const;
export type AddonUploadKind = (typeof ADDON_UPLOAD_KINDS)[number];
export const CACHE_UPLOAD_KINDS = ['questcache', 'creaturecache', 'gameobjectcache', 'hotfix'] as const;
export const ADDON_UPLOAD_KIND_LABELS: Record<AddonUploadKind, string> = {
  addon: 'Addon file',
  questcache: 'Quest cache',
  creaturecache: 'Creature cache',
  gameobjectcache: 'Object cache',
  hotfix: 'Item hotfixes',
};
/**
 * Cache files carry only the client's build number (no `major.minor.patch`); WoW: Forever beta
 * builds start at 69876 (1.60.1) and a cache from an older build is not a Forever cache.
 */
export const FOREVER_MIN_CACHE_BUILD = 69876;
export const FOREVER_CACHE_LINE = '1.60.1';
export function foreverVersionForBuild(build: number): string | null {
  if (!Number.isInteger(build) || build < FOREVER_MIN_CACHE_BUILD) return null;
  return `${FOREVER_CACHE_LINE}.${build}`;
}
/**
 * One observation row per (upload, kind, game id); loot is keyed by its source kind. The
 * `cache_*` and `hotfix_item` kinds hold records read from the client's cache files.
 */
export const ADDON_OBSERVATION_KINDS = [
  'npc',
  'object',
  'loot_npc',
  'loot_object',
  'vendor',
  'trainer',
  'quest',
  'item',
  'cache_quest',
  'cache_npc',
  'cache_object',
  'hotfix_item',
] as const;
export type AddonObservationKind = (typeof ADDON_OBSERVATION_KINDS)[number];

export function isForeverBuild(build: string): boolean {
  return compareBuilds(build, FOREVER_MIN_BUILD) >= 0;
}

const int = z.number().int();
const nonNegInt = int.min(0);
const posInt = int.min(1);
const buildString = z.string().regex(/^\d+\.\d+\.\d+(\.\d+)?$/);
const idKey = z.string().regex(/^\d+$/);
const name = z.string().min(1).max(200);
const unit = z.number().min(0).max(1);

export const sightingSchema = z.object({
  /** Client uiMap id the position is relative to. */
  m: posInt,
  x: unit,
  y: unit,
  /** Epoch seconds. */
  t: nonNegInt,
  /** m = mouseover, t = target, i = interaction (gossip, vendor, trainer, quest, loot). */
  k: z.enum(['m', 't', 'i']),
});
export type AddonSighting = z.infer<typeof sightingSchema>;

const sightings = z.array(sightingSchema).max(MAX_SIGHTINGS_PER_ENTITY);

export const npcRecordSchema = z.object({
  name,
  title: z.string().max(200).optional(),
  lmin: nonNegInt.max(100).optional(),
  lmax: nonNegInt.max(100).optional(),
  class: z.enum(['normal', 'elite', 'rareelite', 'rare', 'worldboss']).optional(),
  ctype: z.string().max(50).optional(),
  /** Reaction to the recording player: hostile, neutral, friendly. */
  react: z.enum(['h', 'n', 'f']).optional(),
  /** The recording player's faction group. */
  pf: z.enum(['A', 'H']).optional(),
  s: sightings,
});

export const objectRecordSchema = z.object({ name, s: sightings });

export const lootRecordSchema = z.object({
  /** Loot events (times the source was looted). */
  n: posInt,
  /** Item id -> times seen in a loot event and total quantity. */
  i: z.record(idKey, z.object({ n: posInt, q: nonNegInt })),
});

export const vendorRecordSchema = z.object({
  b: buildString,
  i: z
    .array(
      z.object({
        id: posInt,
        /** Price in copper. */
        p: nonNegInt,
        /** Currency item id when not coin. */
        c: posInt.optional(),
        /** Limited stock count; -1 or absent means unlimited. */
        s: int.min(-1).optional(),
      }),
    )
    .max(500),
});

export const trainerRecordSchema = z.object({
  b: buildString,
  s: z.array(z.object({ id: posInt, c: nonNegInt, l: nonNegInt.max(100) })).max(500),
});

const questEnd = z.object({
  k: z.enum(['n', 'o']),
  id: posInt,
  m: posInt.optional(),
  x: unit.optional(),
  y: unit.optional(),
});

export const questRecordSchema = z.object({
  title: name,
  text: z.string().max(8000).optional(),
  obj: z.string().max(8000).optional(),
  prog: z.string().max(8000).optional(),
  comp: z.string().max(8000).optional(),
  lvl: nonNegInt.max(100).optional(),
  req: nonNegInt.max(100).optional(),
  money: nonNegInt.optional(),
  xp: nonNegInt.optional(),
  /** Reward item ids and choice item ids. */
  ri: z.array(posInt).max(20).optional(),
  rc: z.array(posInt).max(20).optional(),
  giver: questEnd.optional(),
  ender: questEnd.optional(),
  b: buildString,
  t: nonNegInt,
});

export const itemRecordSchema = z.object({
  name,
  q: int.min(0).max(6),
  il: nonNegInt.optional(),
  rl: nonNegInt.max(100).optional(),
  /** Bind type as the client reports it: 0 none, 1 BoP, 2 BoE, 3 BoU, 4 quest. */
  b: int.min(0).max(4).optional(),
  cls: nonNegInt.optional(),
  sub: nonNegInt.optional(),
  cn: z.string().max(50).optional(),
  sn: z.string().max(50).optional(),
  sell: nonNegInt.optional(),
  lines: z.array(z.string().max(500)).max(40).optional(),
});

// ---- client cache records (docs/plans/2026-09-20-forever-cache-uploads-formats.md) ----

const cacheText = z.string().max(8000);

/** A quest from questcache.wdb. The client's `$B` line breaks arrive as newlines. */
export const cacheQuestSchema = z
  .object({
    title: name,
    /** QuestDescription: the long text the giver says. */
    text: cacheText.optional(),
    /** LogDescription: the objectives summary shown in the quest log. */
    obj: cacheText.optional(),
    /** QuestCompletionLog: the text shown once the quest can be turned in. */
    log: cacheText.optional(),
    lvl: nonNegInt.max(100).optional(),
    req: nonNegInt.max(100).optional(),
    /** QuestSortID: a zone area id when positive. */
    sort: int.optional(),
    money: nonNegInt.optional(),
    /** RewardXPDifficulty, the client's XP bracket index (not an XP amount). */
    xpd: nonNegInt.optional(),
    ri: z.array(posInt).max(20).optional(),
    rc: z.array(posInt).max(20).optional(),
    next: posInt.optional(),
    /** Objectives: type (0 monster, 1 item, 3 talk, 10/19 area trigger, 14 criteria tree), object id, amount, text. */
    objectives: z.array(z.object({ t: nonNegInt, id: int, n: int, d: z.string().max(255).optional() })).max(40).optional(),
  })
  .strict();
export type CacheQuestRecord = z.infer<typeof cacheQuestSchema>;

/** A creature from creaturecache.wdb. `ctype` is the client's CreatureType id (7 humanoid, 1 beast, ...). */
export const cacheNpcSchema = z
  .object({
    name,
    title: z.string().max(200).optional(),
    ctype: nonNegInt.max(255),
    family: posInt.optional(),
    /** Classification: 0 normal, 1 elite, 3 boss, 4 rare (2 rare elite, 5 trivial elsewhere). */
    class: nonNegInt.max(255),
    displays: z.array(nonNegInt).max(20),
  })
  .strict();
export type CacheNpcRecord = z.infer<typeof cacheNpcSchema>;

/** A game object from gameobjectcache.wdb. `otype` is the client's GameObject type id (3 chest, ...). */
export const cacheObjectSchema = z
  .object({
    name,
    otype: nonNegInt,
    displayId: nonNegInt,
    /** The first four Data fields (lock, loot table, ... depending on the type). */
    data: z.array(nonNegInt).max(4).optional(),
  })
  .strict();
export type CacheObjectRecord = z.infer<typeof cacheObjectSchema>;

/** The raw ItemSparse/Item columns of a hotfixed item; the item builder derives the page fields. */
export const hotfixItemSchema = z
  .object({
    name,
    q: int.min(0).max(6),
    il: nonNegInt.optional(),
    rl: nonNegInt.optional(),
    cls: nonNegInt,
    sub: nonNegInt,
    /** InventoryType. */
    inv: nonNegInt.optional(),
    /** Bonding. */
    b: nonNegInt.optional(),
    sell: nonNegInt.optional(),
    desc: cacheText.optional(),
    /** StatModifier_bonusStat / StatPercentEditor pairs. */
    stats: z.array(z.object({ t: int, p: int })).max(10).optional(),
    /** ItemSparse Flags_1 (caster-weapon bit 0x200). */
    flags1: nonNegInt.optional(),
    /** ItemDelay in ms and DmgVariance. */
    delay: nonNegInt.optional(),
    var: z.number().optional(),
    /** RequiredSkillRank. */
    rsk: nonNegInt.optional(),
    /** Item.IconFileDataID. */
    icon: nonNegInt.optional(),
  })
  .strict();
export type HotfixItemRecord = z.infer<typeof hotfixItemSchema>;

const lootKey = z.string().regex(/^[no]\d+$/);

/**
 * One screenshot the addon's button took, keyed by the file stem the game writes
 * (`WoWScrnShot_MMDDYY_HHMMSS`). The subject is an NPC (`n`), object (`o`) or zone (`z`, by uiMap id).
 */
export const shotSchema = z
  .object({
    k: z.enum(['n', 'o', 'z']),
    id: posInt,
    m: posInt.optional(),
    x: unit.optional(),
    y: unit.optional(),
    /** Epoch seconds. */
    t: nonNegInt,
  })
  .strict();
export type ShotRecord = z.infer<typeof shotSchema>;
export const SHOT_STEM = /^WoWScrnShot_\d{6}_\d{6}$/;

export const addonFileSchema = z
  .object({
    format: z.literal(1),
    version: buildString,
    build: buildString,
    fileId: z.string().regex(/^[0-9a-f]{16}$/),
    created: nonNegInt,
    paused: z.boolean(),
    npcs: z.record(idKey, npcRecordSchema),
    objects: z.record(idKey, objectRecordSchema),
    loot: z.record(lootKey, lootRecordSchema),
    vendors: z.record(idKey, vendorRecordSchema),
    trainers: z.record(idKey, trainerRecordSchema),
    quests: z.record(idKey, questRecordSchema),
    items: z.record(idKey, itemRecordSchema),
    /** `/thottbot probe`: API name -> present. */
    probe: z.record(z.string().max(80), z.boolean()).optional(),
    /** Minimap button position. */
    minimap: z.object({ angle: z.number() }).optional(),
    /** Screenshots taken with the addon's button (`/thottbot shot`). */
    shots: z
      .record(z.string().regex(SHOT_STEM), shotSchema)
      .refine((r) => Object.keys(r).length <= MAX_ADDON_SHOTS, `at most ${MAX_ADDON_SHOTS} screenshots`)
      .optional(),
    /** Screenshot button position. */
    shotButton: z.object({ point: z.string().max(20), x: z.number(), y: z.number() }).optional(),
  })
  .strict();

export type AddonFile = z.infer<typeof addonFileSchema>;
export type AddonNpcRecord = z.infer<typeof npcRecordSchema>;
export type AddonObjectRecord = z.infer<typeof objectRecordSchema>;
export type AddonLootRecord = z.infer<typeof lootRecordSchema>;
export type AddonVendorRecord = z.infer<typeof vendorRecordSchema>;
export type AddonTrainerRecord = z.infer<typeof trainerRecordSchema>;
export type AddonQuestRecord = z.infer<typeof questRecordSchema>;
export type AddonItemRecord = z.infer<typeof itemRecordSchema>;

export interface AddonUploadCounts {
  /** Records read from the file; cache uploads fill the matching entity counts and the extras. */
  read: {
    npcs: number;
    objects: number;
    lootEvents: number;
    vendors: number;
    trainers: number;
    quests: number;
    items: number;
    /** Hotfix cache: Valid rows of tables the site does not import. */
    skippedTables?: number;
    /** Hotfix cache: item ids the file marks as removed. */
    removedIds?: number;
  };
  artifacts?: { added: number; updated: number; skipped: number; removed: number };
  merged?: { sightings: number; lootEvents: number; vendorItems: number; trainerSpells: number };
  revert?: { recomputed: number; removed: number };
}
